// embed within IIFE to avoid global namespace pollution
import {BoltButton, Env} from "./internal/bolt-button";

(function() {
	const { publishableKey, checkoutLinkHost, merchantId, env = "prod" } = document.currentScript!.dataset;

	if (!publishableKey) {
		console.error("Bolt Express Checkout: Publishable Key is required");
		return;
	}
	if (!checkoutLinkHost) {
		console.error("Bolt Express Checkout: Checkout Link Host is required");
		return;
	}
	if (!merchantId) {
		console.error("Bolt Express Checkout: Merchant ID is required");
		return;
	}

	switch (env) {
		case Env.staging: break;
		case Env.prod: break;
		default: {
			console.error("Bolt Express Checkout: Env must be one of [staging, prod]");
			return;
		}
	}

	// Do not replace these with optional chaining as we want to fallback even if the value is falsy but not undefined
	const subscriptions_enabled =
		localStorage.getItem("bolt_subscriptions_enabled") == "true" ||
		window.location.hash == "#bolt-subscriptions" ||
		document.querySelector("body")?.dataset?.boltSubscriptionsAlwaysOn == "true";
	const native_buy_enabled = localStorage.getItem("native_buy_enabled") == "true" || window.location.hash == "#native-buy";
	const bolt_buy_enabled =
		localStorage.getItem("bolt_buy_enabled") == "true" ||
        window.location.hash == "#bolt-buy" ||
		document.querySelector("body")?.dataset?.boltButtonAlwaysOn == "true";
	if (!bolt_buy_enabled || native_buy_enabled) {
		console.log("Bolt Express Checkout: Bolt Buy is disabled");
		return;
	}
	const redirect = true; // was isSafari() but iframe is messing with our redirects so switching back to always redirect
	const bolt = new BoltButton(publishableKey, checkoutLinkHost, merchantId, env, redirect);

	document.addEventListener("DOMContentLoaded", function() {
		bolt.installContainer();
		if (subscriptions_enabled) {
			const selectElem = <HTMLSelectElement>document.getElementById("subscription-plans");
			bolt.installSubscriptionPlanSelect(selectElem);
		}
		const buttons = document.querySelectorAll<HTMLButtonElement>("[data-bolt-buy-mode]");
		let cartDrawerBtn = null;
		for (const btn of buttons) {
			switch (btn.dataset?.boltBuyMode!) {
				case "cart": {
					bolt.installCartButton(btn);
					break;
				}
				case "cart-drawer": {
					bolt.installCartButton(btn);
					cartDrawerBtn = btn;
					break;
				}
				case "buy-now": {
					bolt.installBuyNowButton(btn);
					break;
				}
				case "subscribe": {
					bolt.installSubscribeButton(btn);
					break;
				}
				default: {
					console.error("Bolt Express Checkout: Invalid mode", btn.dataset?.boltBuyMode!);
				}
			}
		}

		if (cartDrawerBtn) {
			addCartDrawerObserver();
		}

		addOOSProductObserver();
		addQuickAddModalObserver();

		document.body.classList.add("bolt-buy");
		if (subscriptions_enabled) {
			document.body.classList.add("bolt-subscriptions");
		}

		function addCartDrawerObserver() {
			// cart drawer has its DOM subtree replaced when changing quantity so we need to re-instantiate click handler somehow after it's lost
			const refreshCartDrawerClickHandler = debounce(() => {
				const btn = document.querySelector<HTMLButtonElement>("[data-bolt-buy-mode=cart-drawer]");
				if (btn) {
					bolt.installCartButton(btn);
				}
			}, 100);

			const isQuantityMutation = (mutation: MutationRecord): boolean => {
				if (mutation.type !== "childList") {
					return false;
				}
				return Array.from(mutation.removedNodes).some((n) => {
					if (n.nodeType === n.ELEMENT_NODE) {
						const elem = <HTMLElement>n;
						return elem.classList?.contains("drawer__footer") || elem.classList?.contains("drawer__inner")
					}
					return false;
				});
			}

			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (isQuantityMutation(mutation)) {
						refreshCartDrawerClickHandler();
					}
				});
			});

			observer.observe(document.querySelector("cart-drawer")!, {
				subtree: true,
				childList: true,
			});
		}

		function addOOSProductObserver() {
			// handle OOS products - track Shopify add to cart button and disable our button accordingly
			const addToCartBtn = document.querySelector<HTMLButtonElement>("button[type=submit][name=add]");
			const buyBtn = document.querySelector<HTMLButtonElement>("[data-bolt-buy-mode=buy-now]");
			const subBtn = document.querySelector<HTMLButtonElement>("[data-bolt-buy-mode=subscribe]");
			if (addToCartBtn) {
				const syncPdpButtons = debounce(() => {
					if (buyBtn) {
						buyBtn.disabled = addToCartBtn.disabled;
					}
					if (subBtn) {
						subBtn.disabled = addToCartBtn.disabled;
					}
				}, 50);
				const oosObserver = new MutationObserver(() => syncPdpButtons())
				oosObserver.observe(addToCartBtn, {
					subtree: false,
					attributeFilter: ["disabled"]
				})
				// initial sync if variant loaded is OOS already
				syncPdpButtons();
			}
		}

		function addQuickAddModalObserver() {
			// handle quick add modal (entire DOM of the modal is replaced each time it opens)
			const quickAddModalContainers = document.querySelectorAll(".quick-add-modal__content-info");
			if (quickAddModalContainers.length < 1) {
				return;
			}

			const quickAddObserver = new MutationObserver(debounce(() => {
				const btn = document.querySelector<HTMLButtonElement>("[data-bolt-buy-mode=buy-now]");
				if (btn) {
					bolt.installBuyNowButton(btn);
				}
			}, 100));

			quickAddModalContainers.forEach(container => {
				quickAddObserver.observe(container, {
					subtree: true,
					childList: true,
				});
			})
		}
	});

    function debounce<T extends (...args: any[]) => void>(
        func: T,
        delay: number
    ) {
        let timeout: ReturnType<typeof setTimeout>;
        return (...args: Parameters<T>) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                func(...args);
            }, delay);
        };
    }
})();
